<template>
  <div class="program-settings row column">

    <h1 class="page-header text-center">loyalty points</h1>

    <loading-icon v-if="isProgramLoading" />

    <div v-else class="w-80 small-centered">
      <div class="sb-row bottom-spacing">
        <label>loyalty points enabled</label>
        <sb-checkbox
          :checked="loyaltyEnabled"
          :disabled="!canEditSettings"
          name="merchant[loyalty]"
          @update:checked="loyaltyEnabled=!loyaltyEnabled"
        />
      </div>

      <div v-show="loyaltyEnabled" class="loyalty-wrapper">
        <div class="sb-row bottom-spacing">
          <label for="per-visit">assign loyalty points per <strong>visit</strong></label>
          <input
            type="radio"
            v-model="programType"
            value="points_per_visit"
            name="merchant[program_type]"
            id="per-visit"
            class="points-radio"
            :disabled="!canEditSettings"
          />
        </div>

        <div class="sb-row bottom-spacing">
          <label for="per-dollar">assign loyalty points per <strong>dollar spent</strong></label>
          <input
            type="radio"
            v-model="programType"
            value="points_per_dollar"
            name="merchant[program_type]"
            id="per-dollar"
            class="points-radio"
            :disabled="!canEditSettings"
            @change="pointsPerVisit = Math.floor(pointsPerVisit) || 1"
          />
        </div>

        <div v-if="programType === 'points_per_dollar'" class="bottom-spacing">
          <div class="sb-row start">
            <input
              type="number"
              v-model="pointsPerDollar"
              step="0.01" min="0.01"
              name="merchant[points_per_dollar]"
              id="ppd-number-field"
              class="small margin-right pr-0"
              :class="{ 'has-error': ppdMinError || ppdDecError }"
              :disabled="!canEditSettings"
            />
            <label for="ppd-number-field">{{ pointsPerDollar | pluralize('point') }} awarded per dollar spent</label>
          </div>

          <span class="form-field-notice error" v-if="ppdMinError">a minimum of .01 points is allowed</span>
          <span class="form-field-notice error" v-else-if="ppdDecError">only 2 decimal places allowed</span>
        </div>

        <div v-if="programType === 'points_per_visit'" class="bottom-spacing">
          <div class="sb-row start">
            <input
              type="number"
              v-model="pointsPerVisit"
              step="1" min="1"
              name="merchant[points_per_dollar]"
              id="ppv-number-field"
              class="small margin-right pr-0"
              :class="{'has-error': ppvMinError || ppvDecError}"
              :disabled="!canEditSettings"
            />
            <label for="ppv-number-field">{{ pointsPerVisit | pluralize('point') }} awarded per visit</label>
          </div>

          <span class="form-field-notice error" v-if="ppvMinError">a minimum of 1 point is allowed</span>
          <span class="form-field-notice error" v-else-if="ppvDecError">decimal values are not allowed</span>
        </div>

        <!-- <div class="sb-row bottom-spacing">
          <label>points for cash conversion enabled</label>
          <sb-checkbox
            :checked="pointsCashConversionEnabled"
            :disabled="!canEditSettings"
            name="merchant[points_cash_conversion_enabled]"
            @update:checked="pointsCashConversionEnabled=!pointsCashConversionEnabled"
          />
        </div> -->

        <div class="sb-row start">
          <label for="dpp-number-field">how many points need to be redeemed to be worth $1</label>
          <input
            type="number"
            v-model="dollarsPerPoint"
            step="1" min="0"
            name="merchant[dollars_per_point]"
            id="dpp-number-field"
            class="small margin-right pr-0"
            :class="{ 'has-error': dppMinError }"
            :disabled="!canEditSettings"
          />
        </div>
        <span class="form-field-notice error" v-if="dppMinError">a minimum of 0 points is allowed</span>
      </div>
    </div>

    <div v-if="loyaltyEnabled" class="collapsible-sections mx-10">
      <collapsible-section
        title="bonus engine"
        :expanded.sync="isBonusEngineExpanded"
      >
        <bonus-engine
          :bonusRules.sync="bonusRules"
          :bonusRulesInit="bonusRulesInit"
          :isEditable="canEditSettings"
          :programType="programType"
          @errorChanged="hasErr => hasBonusEngineError = hasErr"
          class="bonus-engine"
        />
      </collapsible-section>
      <collapsible-section
        title="manage loyalty by location"
        :expanded.sync="isLoyaltyByLocationExpanded"
        v-show="{hasLocations}"
      >
        <loyalty-by-location
          :hasLocations.sync="hasLocations"
          :isEditable="canEditSettings"
          :locationsDelta.sync="locationsDelta"
          class="loyalty-by-location"
        />
      </collapsible-section>
      <collapsible-section
        title="points expiration"
        :expanded.sync="isPointsExpirationExpanded"
      >
        <points-expiration
          :expirationOptions.sync="expirationOptions"
          :isEditable="canEditSettings"
          @errorChanged="hasErr => hasExpirationError = hasErr"
          class="points-expiration"
        />
      </collapsible-section>
    </div>

    <div v-if="showNotice" class="callout notice">settings updated</div>
    <div class="sb-row center" :class="{'mb-32':isBonusEngineExpanded}">
      <button
        v-if="canEditSettings"
        :disabled="submitting || hasError"
        @click="clickedSubmit"
        class="action-button button proceed"
      >
        update
      </button>
    </div>

    <modal
      v-if="showWarningModal"
      :showClose="false"
      :wrapperClasses="['expiration-modal','confirm-send']"
    >
      <h3 slot="header">are you sure you want to set up points expiration?</h3>

      <div slot="body">
        <div v-if="expirationOptions.expirationType === 'time based'">
          <span>
            once points expiration is set up, all customer points collected prior to {{ startDate }} will be
            erased and the next point expiration period will be {{ endDate }} so members can only spend points
            accumulated in between
          </span>
          <div class="bottom-spacing">
            <strong>{{ startDate }} - {{ endDate }}</strong>
          </div>
          <div class="bottom-spacing">
            set points to expire the 1st of every
            {{ expirationOptions.monthlyInterval }} {{ expirationOptions.monthlyInterval | pluralize('month') }}?
          </div>
        </div>

        <div v-if="expirationOptions.expirationType === 'visit based' && expirationOptions.visitBasedType === 'days_since_last_visit'">
          once points expiration is set up, all customers who have not recorded a visit in the last
          <strong>{{ expirationOptions.daysSinceLastVisit }}</strong> {{ expirationOptions.daysSincePointsIssued | pluralize('day') }}
          will have their points set to 0, and whenever a customer goes
          <strong>{{ expirationOptions.daysSinceLastVisit }}</strong> {{ expirationOptions.daysSinceLastVisit | pluralize('day') }}
          without recording a visit, their points total will be set to 0.
          <span class="notice">
            set points to expire after {{ expirationOptions.daysSinceLastVisit }}
            {{ expirationOptions.daysSinceLastVisit | pluralize('day') }} without a visit?
          </span>
        </div>

        <div v-if="expirationOptions.expirationType === 'visit based' && expirationOptions.visitBasedType === 'days_since_points_issued'">
          once points expiration is set up, all points acquired more than
          <strong>{{ expirationOptions.daysSincePointsIssued }} {{ expirationOptions.daysSincePointsIssued | pluralize('day') }}</strong>
          ago will be erased and can no longer be redeemed by your members
          <span class="notice">
            set visit points to expire after
            <strong>{{ expirationOptions.daysSincePointsIssued }} {{ expirationOptions.daysSincePointsIssued | pluralize('day') }}</strong>?
          </span>
        </div>
      </div>

      <div class="sb-row center bottom-spacing" slot="action">
        <button class="action-button button proceed" @click.prevent="submitForm()" :disabled="submitting">
          set expiration
        </button>
        <span class="mx-5">or</span>
        <button class="action-button button close" @click.prevent="showWarningModal = false" :disabled="submitting">
          cancel
        </button>
      </div>
    </modal>
  </div>
</template>

<script>
import Modal from '../../components/shared/Modal'
import LoadingIcon from "../../components/shared/LoadingIcon"
import ToggleSectionSlide from "../../components/shared/ToggleSectionSlide"
import UserAbilitiesMixin from '../../mixins/UserAbilitiesMixin'
import {currentMerchantAPI} from "../../services/api/currentMerchant"
import {mapGetters} from 'vuex'
import _ from "lodash"
import {CollapsibleSection} from '@springbig/sb-components'
import BonusEngineRule from "../../components/ProgramSettings/BonusEngineRule";
import BonusEngine from "../../components/ProgramSettings/BonusEngine";
import LoyaltyByLocation from "../../components/ProgramSettings/LoyaltyByLocation";
import PointsExpiration from "../../components/ProgramSettings/PointsExpiration";
import { parse, parseISO, format, isValid, startOfMonth, add } from "date-fns";

export default {
  name: "Program",
  data() {
    return {
      bonusRules: [],
      bonusRulesInit: [],
      debounceUpdateMsg: null,
      expirationOptionsInit: {
        expirationType: 'off',
        monthlyInterval: 1,
        visitBasedType: 'days_since_last_visit',
        daysSinceLastVisit: null,
        daysSincePointsIssued: null
      },
      expirationOptions: {},
      hasBonusEngineError: false,
      hasExpirationError: false,
      hasLocations: false,
      isBonusEngineExpanded: false,
      isPointsExpirationExpanded: false,
      isLoyaltyByLocationExpanded: false,
      isProgramLoading: false,
      locationsDelta: new Map(),
      loyaltyEnabled: false,
      pointsPerDollar: null,
      pointsPerVisit: null,
      programType: 'points_per_visit',
      showNotice: false,
      showWarningModal: false,
      submitting: false,
      pointsCashConversionEnabled: false,
      dollarsPerPoint: null,
    }
  },
  mixins: [UserAbilitiesMixin],
  components: {
    LoyaltyByLocation,
    BonusEngine,
    Modal,
    LoadingIcon,
    ToggleSectionSlide,
    CollapsibleSection,
    BonusEngineRule,
    PointsExpiration
  },
  computed: {
    merchantId() {
      return this.$route.params.id
    },
    canEditSettings() {
      return this.hasAccess(this.userAbilitiesValue, 'SETTINGS_EDIT')
    },
    expirationChanged() {
      return !_.isEqual(this.expirationOptions, this.expirationOptionsInit);
    },
    endDate() {
      return format(startOfMonth(add(new Date(), {months:this.expirationOptions.monthlyInterval})), 'MM/dd/yyyy');
    },
    hasError() {
      return this.ppdDecError
        || this.ppdMinError
        || this.ppvMinError
        || this.ppvDecError
        || this.hasExpirationError
        || this.hasBonusEngineError;
    },
    ...mapGetters(['userAbilitiesValue']),
    ppdDecError() {
      const regex = new RegExp("(?=[^\0])(?=^([0-9]+){0,1}(\.[0-9]{1,2}){0,1}$)", 'g')
      return !regex.test(this.pointsPerDollar) && this.programType === "points_per_dollar"
    },
    ppdMinError() {
      return this.pointsPerDollar < 0.01 && this.programType === "points_per_dollar";
    },
    ppvMinError() {
      return this.pointsPerVisit < 1 && this.programType === "points_per_visit";
    },
    dppMinError() {
      return this.dollarsPerPoint < 0;
    },
    ppvDecError() {
      return !Number.isInteger(parseFloat(this.pointsPerVisit));
    },
    startDate() {
      return format(startOfMonth(new Date()), 'MM/dd/yyyy');
    },
  },
  created() {
    this.$store.dispatch('LOAD_CURRENT_USER')
    this.$store.dispatch('LOAD_CURRENT_MERCHANT')
    this.isProgramLoading = true
    currentMerchantAPI.GETProgramSettings().then(settings => {
      this.loyaltyEnabled = settings.loyalty;
      this.programType = settings.program_type;
      this.pointsPerDollar = settings.points_per_dollar;
      this.pointsPerVisit = settings.points_per_visit;
      this.pointsCashConversionEnabled = settings.points_cash_conversion_enabled;
      this.dollarsPerPoint = settings.dollars_per_point;
      this.bonusRules = this.prepareBonusRules(settings.bonus_rules)
      this.bonusRulesInit = _.cloneDeep(this.bonusRules)

      if (settings.points_expiration_type === null) {
        this.expirationOptionsInit.expirationType = "off";
      } else if (settings.points_expiration_type === "days_since_last_visit") {
        this.expirationOptionsInit.expirationType = "visit based";
        this.expirationOptionsInit.daysSinceLastVisit = settings.points_expiration_interval;
        this.expirationOptionsInit.visitBasedType = settings.points_expiration_type;
      } else if (settings.points_expiration_type === "days_since_points_issued") {
        this.expirationOptionsInit.expirationType = "visit based";
        this.expirationOptionsInit.daysSincePointsIssued = settings.points_expiration_interval;
        this.expirationOptionsInit.visitBasedType = settings.points_expiration_type;
      } else if (settings.points_expiration_type === "every_x_months") {
        this.expirationOptionsInit.expirationType = "time based";
        this.expirationOptionsInit.monthlyInterval = settings.points_expiration_interval;
      }

      this.expirationOptions = _.clone(this.expirationOptionsInit);
      this.debounceUpdateMsg = _.debounce(() => this.showNotice = false, 10000)
    }).finally(() => {
      this.isProgramLoading = false
    });
  },
  beforeUnmount() {
    this.debounceUpdateMsg.cancel()
  },
  methods: {
    clickedSubmit() {
      if (this.expirationOptions.expirationType !== "off" && this.expirationChanged) {
        this.showWarningModal = true;
      } else {
        this.submitForm();
      }
    },
    submitForm() {
      const settings = {
        loyalty: this.loyaltyEnabled,
        program_type: this.programType,
        points_per_dollar: this.pointsPerDollar,
        points_per_visit: this.pointsPerVisit,
        points_cash_conversion_enabled: this.pointsCashConversionEnabled,
        dollars_per_point: this.dollarsPerPoint,
        merchant_locations_attributes: Array.from(this.locationsDelta.values())
      };

      if (this.expirationOptions.expirationType === "off") {
        settings.points_expiration_type = null;
      } else if (this.expirationOptions.expirationType === "visit based" && this.expirationOptions.visitBasedType === "days_since_last_visit") {
        settings.points_expiration_type = "days_since_last_visit";
        settings.points_expiration_interval = this.expirationOptions.daysSinceLastVisit;
      } else if (this.expirationOptions.expirationType === "visit based" && this.expirationOptions.visitBasedType === "days_since_points_issued") {
        settings.points_expiration_type = "days_since_points_issued";
        settings.points_expiration_interval = this.expirationOptions.daysSincePointsIssued;
      } else if (this.expirationOptions.expirationType === "time based") {
        settings.points_expiration_type = "every_x_months";
        settings.points_expiration_interval = this.expirationOptions.monthlyInterval;
      }
      settings.bonus_rules_attributes = this.bonusRules.map(rule => {
        const eventRange = {}
        if (rule.event_type === "time_of_day_range") {
          eventRange.event_range_min = this.formatDate12h(rule.event_range_min);
          eventRange.event_range_max = this.formatDate12h(rule.event_range_max);
        }

        return {
          ..._.omit(rule, ['is_valid', 'unique_id', 'conditions']),
          ...eventRange,
          conditions_attributes: rule.conditions.map(condition => _.omit(condition, ['is_valid', 'unique_id', 'event_value_label']) )
        }
      })

      this.expirationOptionsInit = _.clone(this.expirationOptions)
      this.submitting = true;

      currentMerchantAPI.PUTProgramSettings(this.merchantId, {current_merchant: settings})
        .then(res => {
          this.showWarningModal = false;
          this.showNotice = true;
          this.debounceUpdateMsg();
          this.locationsDelta.clear()
          this.bonusRules = this.prepareBonusRules(res.data.bonus_rules)
          this.bonusRulesInit = _.cloneDeep(this.bonusRules)
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    prepareBonusRules(bonusRules) {
      return bonusRules.map(rule => ({
        ...rule,
        is_valid: true,
        disabled_at: null,
        unique_id: _.uniqueId(),
        conditions: rule.conditions.map(condition => ({
          ...condition,
          is_valid: true,
          unique_id: _.uniqueId(),
          event_range_min: condition.event_type === "time_of_day_range" ? this.parseDateString(condition.event_range_min) : condition.event_range_min,
          event_range_max: condition.event_type === "time_of_day_range" ? this.parseDateString(condition.event_range_max) : condition.event_range_max,
        }))
      }))
    },
    parseDateString(dateStr) {
      if(isValid(parseISO(dateStr))) {
        return parseISO(dateStr);
      }else{
        return parse(dateStr, "h:mm aaaaa'm'", new Date());
      }
    },
    formatDate12h(date) {
      return format(date, "h:mm aaaaa'm'")
    }
  },
}
</script>

<style lang="scss" scoped>
.collapsible-sections::v-deep,
.program-settings {
  input:disabled {
    color: #adadad;
    border-color: #adadad;
    background-color: #c8ced354;
  }
}
.collapsible-sections::v-deep {
  margin: 30px 30px 0;
  @media only screen and (max-width: 639px) {
    margin-left:0;
    margin-right:0;
  }
  > main {
    margin-bottom: 30px;
    &:first-child > .collapsing-container {
      width:100%;
    }
  }
  :first-child .collapsing-container {
    max-width: initial;
  }
  .collapsing-container {
    margin: auto;
    max-width: 450px;
    .content-container {
      display: flex;
      flex-direction: column;
      position: relative;
      margin-bottom:4rem;
      > div {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }
    }
  }
}

</style>
